import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Container } from "react-bootstrap"
import { useHistory, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import ActivityTimeline from "@/components/Feed/ActivityTimeline"
import FeedFilters from "@/components/Feed/FeedFilters"
import { FeedFilterValues, FeedType, TYPE_GROUPS, TYPE_STYLE } from "@/components/Feed/types"
import "@/components/Feed/feed.css"

const readFilters = (search: string): FeedFilterValues => {
  const params = new URLSearchParams(search)
  return {
    types: (params.get("types") || "").split(",").filter(Boolean) as FeedType[],
    agentId: params.get("agent") || "",
    search: params.get("q") || "",
    before: params.get("before") || "",
  }
}

const writeFilters = (f: FeedFilterValues) => {
  const params = new URLSearchParams()
  if (f.types.length) params.set("types", f.types.join(","))
  if (f.agentId) params.set("agent", f.agentId)
  if (f.search) params.set("q", f.search)
  if (f.before) params.set("before", f.before)
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

const FeedPage = () => {
  const { t } = useTranslation("global")
  const history = useHistory()
  const location = useLocation()
  const [todayCounts, setTodayCounts] = useState<Record<string, number | null>>({})

  // Keyed on the query string so the filters object stays stable between renders.
  const filters = useMemo(() => readFilters(location.search), [location.search])

  const setFilters = (next: FeedFilterValues) => history.replace({ pathname: location.pathname, search: writeFilters(next) })

  // Exact counts for today, one NewCount call per chip group.
  const loadToday = useCallback(async () => {
    const user: any = parsedUser()
    const after = moment().startOf("day").toISOString()
    const entries = await Promise.all(
      TYPE_GROUPS.map(async g => {
        try {
          const res = await API.getAction(
            "Feed/NewCount",
            `OrganizationId=${user?.OrganizationId}&Types=${g.types.join(",")}&After=${encodeURIComponent(after)}`
          )
          return [g.key, res.data?.status === "ok" ? res.data.response : null] as const
        } catch {
          return [g.key, null] as const
        }
      })
    )
    setTodayCounts(Object.fromEntries(entries))
  }, [])

  useEffect(() => {
    loadToday()
  }, [loadToday])

  return (
    <Container fluid className="px-3 py-3 pb-5">
      <div className="d-flex align-items-center mb-3">
        <h4 className="mb-0">
          <i className="fas fa-stream me-2 text-primary" />
          {t("Actividad")}
        </h4>
      </div>

      <div className="feed-layout">
        <aside className="feed-rail">
          <div className="feed-panel">
            <FeedFilters value={filters} onChange={setFilters} />
          </div>
        </aside>

        <main>
          <ActivityTimeline filters={filters} onReloaded={loadToday} />
        </main>

        <aside className="feed-rail feed-rail-right">
          <div className="feed-panel">
            <div className="feed-filters-title">{t("Hoy")}</div>
            {TYPE_GROUPS.map(g => {
              const style = TYPE_STYLE[g.types[0]]
              const count = todayCounts[g.key]
              return (
                <button
                  key={g.key}
                  type="button"
                  className="feed-summary-row w-100 border-0 bg-transparent px-0"
                  onClick={() => setFilters({ ...filters, types: g.types, before: "" })}
                >
                  <span>
                    <i className={`fas ${style.icon} me-2`} style={{ color: style.color, width: 16 }} />
                    {t(g.label)}
                  </span>
                  <strong>{count == null ? "—" : count}</strong>
                </button>
              )
            })}
          </div>
        </aside>
      </div>
    </Container>
  )
}

export default FeedPage
